import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { TreeNode } from './types';
import { TreeState } from './useTree';
import { ChevronRight } from '@carbon/icons-react';

/** Simple arrow icon (rotates when expanded) */
const Arrow = styled(ChevronRight).withConfig({
  shouldForwardProp: (prop) => prop !== 'collapsed',
  displayName: 'Arrow',
})<{ collapsed?: boolean }>`
  display: inline-block;
  transition: transform 0.2s ease;
  transform: rotate(90deg);
  ${({ collapsed }) =>
    collapsed &&
    css`
      transform: rotate(0);
    `}
`;

/** Container for a single tree item */
const ItemContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['depth', 'isLeaf', 'selected'].includes(prop),
  displayName: 'ItemContainer',
})<{ depth: number; isLeaf: boolean; selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px;
  cursor: pointer;
  gap: 8px;
  /* background-color: #fff; */

  /* indentation based on depth */
  padding-left: ${({ depth }) => depth * 24 + 8}px;

  ${({ selected }) =>
    selected &&
    css`
      background-color: rgba(0, 0, 0, 0.2);
    `}
  border-radius: 8px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    ${({ selected }) =>
      selected &&
      css`
        /* light hover effect */
        background-color: rgba(0, 0, 0, 0.3);
      `}
  }

  ${({ isLeaf }) =>
    isLeaf &&
    css`
      /* if leaf, no arrow, reduce padding for alignment */
      ${Arrow} {
        display: none;
      }
    `}
`;

const ItemInfo = styled.div`
  display: flex;
  gap: 8px;
`;

const ItemActions = styled.div`
  display: flex;
  gap: 8px;
`;

/** Label text styling */
const Label = styled.span`
  font-size: 14px;
  color: #333;
`;

export interface TreeItemProps {
  node: TreeNode;
  state: TreeState;
  depth: number;
  actions?: React.FC<{ id: string }>[];
  onNodeClick?: (id: string) => void;
  onToggleCollapse?: (id: string) => void;
}

export const TreeItem: React.FC<TreeItemProps> = ({
  node,
  depth,
  state,
  actions,
  onNodeClick,
  onToggleCollapse,
}) => {
  // Only nodes with children can toggle expand/collapse
  const isLeaf = !node.children || node.children.length === 0;

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleCollapse?.(node.id);
    },
    [node.id, onToggleCollapse],
  );

  const handleClickLabel = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onNodeClick?.(node.id);
    },
    [node.id, onNodeClick],
  );

  const actionNodes = useMemo(
    () => actions?.map((Action, index) => <Action key={`${node.id}-${index}`} id={node.id} />),
    [actions, node.id],
  );

  const stopPropagation = useCallback((evt: React.MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
  }, []);

  const isCollapsed = state.get(node.id)?.collapsed ?? true;
  const isSelected = state.get(node.id)?.selected ?? false;

  return (
    <>
      <ItemContainer depth={depth} isLeaf={isLeaf} selected={isSelected} onClick={handleClickLabel}>
        <ItemInfo>
          {/* @ts-expect-error type error with styled-components */}
          <Arrow collapsed={isCollapsed} onClick={handleToggle} />
          <Label>{node.label}</Label>
        </ItemInfo>
        <ItemActions onClick={stopPropagation}>{actionNodes}</ItemActions>
      </ItemContainer>

      {!isCollapsed &&
        node.children?.map((child) => (
          <TreeItem
            key={child.id}
            node={child}
            depth={depth + 1}
            state={state}
            onNodeClick={onNodeClick}
            onToggleCollapse={onToggleCollapse}
            actions={actions}
          />
        ))}
    </>
  );
};
